/**
 * One employee-day, opened up beside the team list.
 *
 * A panel rather than a page for the same reason the employee profile is one:
 * the question is asked *of a row* — "what actually happened on this day" —
 * and navigating away to answer it loses the date and the scroll position that
 * prompted it.
 *
 * Shows more per check-in than the employee's own view does: distance, whether
 * the fix was inside the geofence, and any anomaly flags. That is deliberate
 * and is why the endpoint behind this is Manager+ — an employee who can see
 * exactly which checks fired can defeat them more easily.
 */
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { client } from '../api/client';
import type { AttendanceDayDetail, MeetingPeriodDetail, PingDetail } from '../types';
import { flagLabels } from '../lib/anomalyFlags';
import { titleCaseName } from '../lib/titleCase';
import { isWithdrawn, WITHDRAWN_HINT, WITHDRAWN_LABEL } from '../lib/withdrawn';
import { attendanceStatusLabel } from '../lib/attendanceLabels';
import { pingResponseLabel } from '../lib/statusLabels';
import { useDismissOnEscape } from '../lib/useDismiss';

const PING_TONE: Record<string, string> = {
  responded: 'bg-emerald-100 text-emerald-700',
  missed: 'bg-red-100 text-red-700',
  skipped: 'bg-gray-100 text-gray-400',
  scheduled: 'bg-gray-100 text-gray-500',
  sent: 'bg-amber-100 text-amber-700',
};

function clock(value: string): string {
  return new Date(value).toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function duration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

/**
 * Where a check-in was answered from, in words.
 *
 * "not verified" stays its own thing rather than collapsing into "away": a
 * weak GPS fix and somebody somewhere else are different facts, and the geo
 * module is built on not confusing them.
 */
function pingPlace(ping: PingDetail): string {
  if (ping.status !== 'responded') return '';
  if (ping.locationStatus === 'unverified') return 'not verified';
  if (ping.insideGeofence) return 'at workplace';
  if (ping.distanceM == null) return 'away';
  const km = ping.distanceM / 1000;
  return km >= 1 ? `${km.toFixed(1)} km away` : `${Math.round(ping.distanceM)} m away`;
}

function MeetingRow({ meeting }: { meeting: MeetingPeriodDetail }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-1.5">
      <span className="badge bg-indigo-100 text-indigo-700">
        {meeting.meetingType}
      </span>
      <span className="text-sm text-gray-900">
        {clock(meeting.startedAt)} – {meeting.endedAt ? clock(meeting.endedAt) : 'now'}
      </span>
      <span className="text-sm text-gray-500">
        {/* null, not zero: a period still running has not produced a duration
            yet, the same rule the worked-hours figure uses. */}
        {meeting.minutes == null ? 'still running' : duration(meeting.minutes)}
      </span>
      {/* Two different findings, and the narrower one is the one worth
          reading. A break the server closed *at its cap* ran over; a period
          the server closed because the shift ended did not. autoClosed alone
          could not tell them apart. */}
      {meeting.overran ? (
        <span
          className="text-xs font-medium text-amber-700"
          title="Ran past its limit — check-ins started again at that point"
        >
          ran over
        </span>
      ) : (
        meeting.autoClosed && (
          <span className="text-xs text-amber-700" title="Closed By the Server">
            auto-ended
          </span>
        )
      )}
      {meeting.note && (
        <span className="w-full truncate text-xs text-gray-500">{meeting.note}</span>
      )}
    </li>
  );
}

export default function AttendanceDetailPanel({
  userId,
  workDate,
  name,
  mine = false,
  onClose,
}: {
  /* Keyed by person and date rather than by a rollup id.
   *
   * A rollup only exists once a shift ends or the nightly job runs; check-ins
   * do not wait for either. Keying on the rollup meant the days most worth
   * opening -- a shift left running, a day the job never reached -- could not
   * be opened at all, because the key had never been created. */
  userId: string;
  workDate: string;
  /** Shown while the day loads, so the panel is never anonymous. */
  name?: string | null;
  /**
   * Whether this is the viewer's own day.
   *
   * Switches to the self-scoped route, which takes no user id at all -- the
   * day comes off the token. The manager's route is Approver-gated and would
   * 403 for an employee opening their own record, which is the wrong way
   * round for a figure that decides whether they were present.
   *
   * A flag rather than two components: everything below this line is the same
   * panel, and a second copy of it is a second place to forget a column.
   */
  mine?: boolean;
  onClose: () => void;
}) {
  // Escape closes the topmost drawer. Added alongside the month
  // panel's: none of these listened for it, which was consistent and
  // consistently wrong, and giving it to one would have made that one
  // the odd drawer out.
  useDismissOnEscape(onClose);

  const { data, isLoading, isError } = useQuery<AttendanceDayDetail>({
    queryKey: ['attendance-detail', mine ? 'me' : userId, workDate],
    queryFn: async () =>
      (
        await client.get(
          mine
            ? '/api/v1/attendance/me/day-detail'
            : '/api/v1/attendance/day-detail',
          // The self route takes no id -- that is its authorisation. Sending
          // one anyway would be harmless and misleading.
          { params: mine ? { workDate } : { userId, workDate } },
        )
      ).data,
    retry: false,
  });

  const day = data?.day;

  return (
    <div className="fixed inset-0 z-30 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        className="h-full w-full max-w-lg overflow-y-auto bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              {titleCaseName(day?.userName) ?? name ?? 'Attendance'}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">{day?.workDate ?? workDate}</p>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : isError || !data ? (
          <p className="p-4 text-sm text-gray-500">
            That day could not be loaded.
          </p>
        ) : (
          <div className="space-y-5 p-4">
            {!day && (
              /* Said plainly rather than shown as zeroes. Nothing scored this
                 day -- the shift may still be open, or the nightly job may not
                 have reached it -- so there are no totals to give. The
                 check-ins below are real regardless. */
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                This day has not been scored, so there are no totals for it yet.
                The check-ins below are what was actually recorded.
              </p>
            )}

            {day && (
            <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="badge bg-gray-100 text-gray-700">
                {attendanceStatusLabel(day.status)}
              </span>
              {isWithdrawn(day) ? (
                <span
                  className="text-sm text-amber-700"
                  title={day.scoringWithdrawnReason ?? WITHDRAWN_HINT}
                >
                  {WITHDRAWN_LABEL}
                </span>
              ) : (
                <span className="text-sm text-gray-600">
                  {Number(day.availabilityPct).toFixed(0)}% available
                </span>
              )}
              <span className="text-sm text-gray-500">
                {day.respondedPings}/{day.expectedPings} answered
              </span>
            </div>

            <dl className="divide-y divide-gray-100 text-sm">
              <div className="flex gap-3 py-1.5">
                <dt className="w-40 shrink-0 text-xs text-gray-500">Started</dt>
                <dd className="text-gray-900">
                  {day.lateByMinutes != null && day.lateByMinutes > 0 ? (
                    <span className="text-amber-700">
                      {duration(day.lateByMinutes)} late
                    </span>
                  ) : (
                    <span className="text-gray-400">on time</span>
                  )}
                </dd>
              </div>
              <div className="flex gap-3 py-1.5">
                <dt className="w-40 shrink-0 text-xs text-gray-500">Started from</dt>
                <dd className="text-gray-900">
                  {day.startLocationStatus ?? <span className="text-gray-300">—</span>}
                </dd>
              </div>
              <div className="flex gap-3 py-1.5">
                <dt className="w-40 shrink-0 text-xs text-gray-500">Time on the clock</dt>
                <dd className="text-gray-900">{duration(day.workedMinutes)}</dd>
              </div>
              <div className="flex gap-3 py-1.5">
                <dt className="w-40 shrink-0 text-xs text-gray-500">In meetings</dt>
                <dd className="text-gray-900">{duration(data.meetingMinutes)}</dd>
              </div>
              {/* Kept apart from meeting time on purpose: three hours with a
                  client and three hours of lunch are not the same finding, and
                  one "paused" total would report them identically. */}
              <div className="flex gap-3 py-1.5">
                <dt className="w-40 shrink-0 text-xs text-gray-500">On breaks</dt>
                <dd className="text-gray-900">
                  {duration(data.breakMinutes)}
                  {data.breakOverruns > 0 && (
                    <span className="ml-2 text-xs text-amber-700">
                      {data.breakOverruns} ran over
                    </span>
                  )}
                </dd>
              </div>
            </dl>
            </>
            )}

            <section>
              <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Meetings and Breaks
              </h4>
              {data.meetings.length === 0 ? (
                <p className="mt-1 text-sm text-gray-400">None.</p>
              ) : (
                <ul className="mt-1 divide-y divide-gray-100">
                  {data.meetings.map((m) => (
                    <MeetingRow key={m.id} meeting={m} />
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Check-ins ({data.pings.length})
              </h4>
              {/* Skipped and missed must not read alike: one is the system
                  declining to ask, the other is a question that went
                  unanswered. Only the second is anybody's fault -- and a
                  skipped one now says which of the three reasons it was.

                  The flag count is gone. It read "2 flags" on every row of
                  ninety and named neither, so there was nothing to do with
                  it; the names were on a hover title nobody finds. The data
                  is still computed and stored, and bringing it back means
                  showing which flags fired. */}
              <ul className="mt-1 divide-y divide-gray-100">
                {data.pings.map((ping) => (
                  <li key={ping.id} className="flex flex-wrap items-baseline gap-x-2 py-1.5">
                    <span className="w-16 shrink-0 tabular-nums text-sm text-gray-900">
                      {clock(ping.dueAt)}
                    </span>
                    <span
                      className={`badge ${PING_TONE[ping.status] ?? 'bg-gray-100 text-gray-500'}`}
                    >
                      {ping.status}
                    </span>
                    {ping.response && (
                      <span className="text-sm text-gray-700">
                        {pingResponseLabel(ping.response)}
                      </span>
                    )}
                    {pingPlace(ping) && (
                      <span className="text-xs text-gray-500">{pingPlace(ping)}</span>
                    )}
                    {/* Why it was never asked. "skipped" alone made the
                        reader guess between a meeting, a break and the end of
                        the shift -- three different findings wearing one
                        word, and only the reader could tell them apart by
                        cross-referencing the times above. */}
                    {ping.skipReason && (
                      <span className="text-xs text-gray-500">
                        {ping.skipReason}
                      </span>
                    )}
                    {/* What the integrity checks noticed, in words.
                        Withheld from this payload until now because the only
                        thing any screen did with it was count it — and a
                        count is the one form of it nobody can act on.
                        Amber rather than red: every one of these is evidence
                        for a person to read, and a bad GPS fix in a basement
                        looks identical to a spoofed one. */}
                    {ping.anomalyFlags && ping.anomalyFlags.length > 0 && (
                      <span className="text-xs text-amber-700">
                        {flagLabels(ping.anomalyFlags)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              {data.pings.length === 0 && (
                <p className="mt-1 text-sm text-gray-400">
                  No check-ins were scheduled for this day.
                </p>
              )}
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}
